import {
    FC,
    PropsWithChildren,
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import {
    IBonusRange,
    IDecardProviderConfig,
    IMessagingConfig,
    IMultibetBonusLimit,
    IPaymentsCurrencyConfig,
    IBasicProviderConfig,
    ISiteConfigs,
    IUsersRegistrationConfig,
    OddsFormat,
    IBetprocessorAmountConfig,
    ITaxConfig,
} from "@finbackoffice/clientbff-client";
import {
    BonusType,
    BonusVertical,
    Country,
    Currency,
    DeviceType,
    EventStatus,
    FeedType,
    PaymentProvider,
} from "@finbackoffice/enums";
import { CountryISO2Mapping, DEFAULT_SPORTS_ORDER } from "@finbackoffice/fe-core";
import {
    AuthContext,
    BrowserStorageContext,
    ClientBFFContext,
    BrowserStorageIdsEnum,
    useDidUpdateEffect,
    useRuntimeConfig,
} from "@finbackoffice/site-core";
import { LEGAL_AGE } from "utils/constants";

export enum SiteDirection {
    Rtl = "rtl",
    Ltr = "ltr",
}

export interface IConfigContextV2 {
    changeOddFormat: (value: string) => void;
    loadSiteConfig: () => Promise<void>;
    oddFormat: string;
    siteConfigs: ISiteConfigs | null;
    siteConfigReady: boolean;
    siteDefaultCurrency: Currency;
    siteSupportedOddsFormat: OddsFormat[] | undefined;
    siteDefaultOddsFormat: OddsFormat | undefined;
    sitePaymentsCurrencyConfig?: Partial<Record<Currency, IPaymentsCurrencyConfig>>;
    siteRegistrationConfig: IUsersRegistrationConfig | undefined;
    siteMessagingConfig: IMessagingConfig | undefined;
    betSlipMaxSelections: number;
    siteMaxOdds: number;
    siteSupportedCurrencies: Currency[];
    isBetProcessorEnabled: boolean;
    betProcessorFeedType: FeedType;
    betProcessorMinBet: IBetprocessorAmountConfig | null;
    betProcessorMaxBet: IBetprocessorAmountConfig | null;
    betProcessorMaxWin: IBetprocessorAmountConfig | null;
    betProcessorTax: ITaxConfig | null;
    siteClientCountry: {
        isoAlpha2: string;
        isoAlpha3: Country;
    };
    siteCurrentOddFormat: string;
    isSportMultiBetBonusesEnabled: boolean;
    sportMultiBetBonusRanges: IBonusRange[];
    sportMultiBetMinOdds: string;
    sportMultiBetLimits: IMultibetBonusLimit | null;
    sportMultiBetCategories: (EventStatus.Prematch | EventStatus.Live)[];
    decardPaymentConfig: IDecardProviderConfig | null;
    gumBallPayPaymentConfig: IDecardProviderConfig | null;
    bankTransferPaymentConfig: IBasicProviderConfig | null;
    cryptoPaymentConfig: IBasicProviderConfig | null;
    sportsOrder: number[];
    isPhoneVerificationRequired: boolean;
    isEmailVerificationRequired: boolean;
    isIdVerificationRequired: boolean;
    legalAge: number;
    siteDirection: SiteDirection;
    deviceType: DeviceType;
    referralCode?: string;
}

export const ConfigContextV2 = createContext<IConfigContextV2>(null as any);

type Props = {
    defaultConfig: ISiteConfigs;
    defaultCountry: Country;
    siteDirection: SiteDirection;
    deviceType: DeviceType;
    referralCode?: string;
};

type IConfigState = {
    data: ISiteConfigs;
    loading: boolean;
    error: string;
};

export const ConfigProviderV2: FC<Props & PropsWithChildren> = ({
    children,
    defaultConfig,
    defaultCountry,
    siteDirection,
    deviceType,
    referralCode,
}) => {
    const domain = useRuntimeConfig("DOMAIN");

    const { userToken, prevToken } = useContext(AuthContext);
    const { get } = useContext(BrowserStorageContext);
    const client = useContext(ClientBFFContext);

    const [config, setConfig] = useState<IConfigState>({
        data: defaultConfig,
        loading: false,
        error: "",
    });
    const [oddFormat, setOddFormat] = useState("");

    const siteConfigReady = useMemo(() => !config.loading, [config.loading]);
    const siteCurrentOddFormat = useMemo(() => oddFormat, [oddFormat]);

    const sitePaymentsCurrencyConfig = useMemo(
        () => config.data?.payments?.currency,
        [config.data?.payments?.currency],
    );

    const siteDefaultCurrency = useMemo(
        () => config.data?.currency.default,
        [config.data?.currency.default],
    );

    const siteSupportedCurrencies = useMemo(
        () => config.data?.currency?.supported,
        [config.data?.currency.supported],
    );

    const siteSupportedOddsFormat = useMemo(
        () => config.data?.odds_format?.supported,
        [config.data?.odds_format?.supported],
    );

    const siteDefaultOddsFormat = useMemo(
        () => config.data?.odds_format?.default,
        [config.data?.odds_format?.default],
    );

    const siteRegistrationConfig = useMemo(
        () => config.data?.users?.registration,
        [config.data?.users?.registration],
    );

    const siteMessagingConfig = useMemo(
        () => config.data?.message?.messaging,
        [config.data?.message?.messaging],
    );

    const betSlipMaxSelections = useMemo(
        () => config.data?.betprocessor?.max_selections ?? 0,
        [config.data?.betprocessor],
    );

    const siteMaxOdds = useMemo(
        () => config.data?.betprocessor?.max_odds ?? 0,
        [config.data?.betprocessor],
    );

    const betProcessorMaxWin = useMemo(
        () => config.data?.betprocessor?.max_win ?? null,
        [config.data?.betprocessor],
    );

    const betProcessorMaxBet = useMemo(
        () => config.data.betprocessor.max_bet ?? null,
        [config.data.betprocessor.max_bet],
    );

    const betProcessorMinBet = useMemo(
        () => config.data?.betprocessor?.min_bet ?? null,
        [config.data?.betprocessor],
    );

    const betProcessorFeedType = useMemo(
        () => config.data.betprocessor.feed_type,
        [config.data.betprocessor.feed_type],
    );

    const betProcessorTax = useMemo(
        () => config.data.betprocessor.tax ?? null,
        [config.data.betprocessor.tax],
    );

    const isBetProcessorEnabled = useMemo(
        () => config.data?.betprocessor?.enabled ?? false,
        [config.data?.betprocessor],
    );

    const siteClientCountry = useMemo(
        () =>
            config.data?.country || {
                isoAlpha2: CountryISO2Mapping[defaultCountry],
                isoAlpha3: defaultCountry,
            },
        [config.data?.country, defaultCountry],
    );

    const sportMultiBetBonus = useMemo(
        () => config.data?.bonus[BonusType.Multibet]?.[BonusVertical.Sport] || null,
        [config.data?.bonus],
    );

    const isSportMultiBetBonusesEnabled = useMemo(() => {
        const isBrandAllowed =
            sportMultiBetBonus?.allowed_brands.length === 0 ||
            sportMultiBetBonus?.allowed_brands.some((brand) => domain.endsWith(brand)) ||
            false;

        return domain.includes("localhost")
            ? !!sportMultiBetBonus?.enabled
            : isBrandAllowed && !!sportMultiBetBonus?.enabled;
    }, [domain, sportMultiBetBonus?.allowed_brands, sportMultiBetBonus?.enabled]);

    const sportMultiBetBonusRanges = useMemo(
        () => sportMultiBetBonus?.ranges || [],
        [sportMultiBetBonus?.ranges],
    );

    const sportMultiBetMinOdds = useMemo(
        () => sportMultiBetBonus?.min_odds || "",
        [sportMultiBetBonus?.min_odds],
    );

    const sportMultiBetLimits = useMemo(
        () => sportMultiBetBonus?.limit || null,
        [sportMultiBetBonus?.limit],
    );

    const sportMultiBetCategories = useMemo(
        () => sportMultiBetBonus?.category || [],
        [sportMultiBetBonus?.category],
    );

    const decardPaymentConfig = useMemo(
        () => config.data?.payments[PaymentProvider.Decard] ?? null,
        [config.data?.payments],
    );

    const gumBallPayPaymentConfig = useMemo(
        () => config.data?.payments[PaymentProvider.GumBallPay] ?? null,
        [config.data?.payments],
    );

    const bankTransferPaymentConfig = useMemo(
        () => config.data?.payments[PaymentProvider.BankTransfer] ?? null,
        [config.data?.payments],
    );

    const cryptoPaymentConfig = useMemo(
        () => config.data?.payments[PaymentProvider.Vaultody] ?? null,
        [config.data?.payments],
    );

    const isPhoneVerificationRequired = useMemo(
        () => siteRegistrationConfig.require_phone_verification || false,
        [siteRegistrationConfig.require_phone_verification],
    );

    const isEmailVerificationRequired = useMemo(
        () => siteRegistrationConfig.require_email_verification || false,
        [siteRegistrationConfig.require_email_verification],
    );

    const isIdVerificationRequired = useMemo(
        () => siteRegistrationConfig.require_id_verification || false,
        [siteRegistrationConfig.require_id_verification],
    );

    const legalAge = useMemo(
        () => siteRegistrationConfig.legal_age || LEGAL_AGE,
        [siteRegistrationConfig.legal_age],
    );

    const sportsOrder = useMemo(
        () => (betProcessorFeedType === FeedType.BetConstruct ? [] : DEFAULT_SPORTS_ORDER),
        [betProcessorFeedType],
    );

    useEffect(() => {
        const storedOddsFormat =
            (get(BrowserStorageIdsEnum.ODD_FORMAT) as OddsFormat) || siteDefaultOddsFormat;
        if (storedOddsFormat) {
            setOddFormat(storedOddsFormat);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useDidUpdateEffect(() => {
        if (userToken !== prevToken) {
            void loadSiteConfig();
        }
    }, [userToken]);

    const changeOddFormat = useCallback((value: string) => {
        setOddFormat(value);
        localStorage.setItem(BrowserStorageIdsEnum.ODD_FORMAT, value);
    }, []);

    const loadSiteConfig = useCallback(async () => {
        setConfig((state) => ({
            ...state,
            loading: true,
        }));

        const response = await client.getSiteConfig();

        setConfig((state) => ({
            ...state,
            data: response,
            loading: false,
        }));
    }, [client]);

    const value = useMemo(
        () => ({
            changeOddFormat,
            oddFormat,
            siteConfigs: config.data,
            loadSiteConfig,
            siteDefaultCurrency,
            siteSupportedOddsFormat,
            siteDefaultOddsFormat,
            sitePaymentsCurrencyConfig,
            siteRegistrationConfig,
            siteMessagingConfig,
            betSlipMaxSelections,
            siteMaxOdds,
            isBetProcessorEnabled,
            betProcessorFeedType,
            betProcessorTax,
            betProcessorMinBet,
            betProcessorMaxWin,
            betProcessorMaxBet,
            siteClientCountry,
            siteSupportedCurrencies,
            siteConfigReady,
            siteCurrentOddFormat,
            isSportMultiBetBonusesEnabled,
            sportMultiBetBonusRanges,
            sportMultiBetMinOdds,
            sportMultiBetLimits,
            sportMultiBetCategories,
            decardPaymentConfig,
            gumBallPayPaymentConfig,
            bankTransferPaymentConfig,
            cryptoPaymentConfig,
            sportsOrder,
            isPhoneVerificationRequired,
            isEmailVerificationRequired,
            isIdVerificationRequired,
            legalAge,
            siteDirection,
            deviceType,
            referralCode,
        }),
        [
            changeOddFormat,
            oddFormat,
            config.data,
            loadSiteConfig,
            siteDefaultCurrency,
            siteSupportedOddsFormat,
            siteDefaultOddsFormat,
            sitePaymentsCurrencyConfig,
            siteRegistrationConfig,
            siteMessagingConfig,
            betSlipMaxSelections,
            siteMaxOdds,
            isBetProcessorEnabled,
            betProcessorFeedType,
            betProcessorTax,
            betProcessorMinBet,
            betProcessorMaxWin,
            betProcessorMaxBet,
            siteClientCountry,
            siteSupportedCurrencies,
            siteConfigReady,
            siteCurrentOddFormat,
            isSportMultiBetBonusesEnabled,
            sportMultiBetBonusRanges,
            sportMultiBetMinOdds,
            sportMultiBetLimits,
            sportMultiBetCategories,
            decardPaymentConfig,
            gumBallPayPaymentConfig,
            bankTransferPaymentConfig,
            cryptoPaymentConfig,
            sportsOrder,
            isPhoneVerificationRequired,
            isEmailVerificationRequired,
            isIdVerificationRequired,
            legalAge,
            siteDirection,
            deviceType,
            referralCode,
        ],
    );

    return <ConfigContextV2.Provider value={value}>{children}</ConfigContextV2.Provider>;
};
